import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { authAPI } from '../../services/api';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const [authState, setAuthState] = useState<'checking' | 'authorized' | 'unauthorized' | 'not_authenticated'>('checking');

  const checkAdminStatus = useCallback(async () => {
    // Check if we have a token
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setAuthState('not_authenticated');
      return;
    }

    try {
      // Always fetch fresh profile from API to check admin status
      const response = await authAPI.getProfile();
      const userData = response.data;
      
      // Check admin status from fresh API data
      const isAdmin = userData?.role === 'admin' || userData?.is_staff === true;
      
      if (isAdmin) {
        // Update store with fresh data
        useAuthStore.setState({ user: userData, isAuthenticated: true });
        setAuthState('authorized');
      } else {
        setAuthState('unauthorized');
      }
    } catch (error) {
      // Token invalid or expired
      setAuthState('not_authenticated');
    }
  }, []);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  // Show loading while checking
  if (authState === 'checking' || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (authState === 'not_authenticated') {
    return <Navigate to="/login" state={{ message: 'Please login as admin to access this page' }} replace />;
  }

  if (authState === 'unauthorized') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
